import { c as _c } from "react/compiler-runtime";
import React, { FocusEvent, MouseEvent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import useRoundDown from "@/app/utils/formatAmount";
import type { AmountInputProps } from "../input.interface";
import type { AmountInputViewModel } from "./types";
import { balanceClickDisplayValue, normalizePropValue, parseRawValue, shouldPreserveUserInput } from "./amountInput.utils";
export function useAmountInput(t0) {
  const $ = _c(71);
  const {
    value,
    onChange,
    onEmpty,
    onKeyDown,
    onFocus,
    onBlur,
    type,
    label,
    placeholder,
    decimalLimit,
    suffix,
    prefix,
    isLogin: t1,
    balanceSymbol,
    walletBalance,
    minAllowed,
    maxAllowed,
    hasError: t2,
    errorTextMin,
    errorTextMax,
    hintText,
    showActionButton: t3,
    onActionButtonClick,
    selectedCoin,
    inputStyle: t4,
    customInputClass: t5,
    customFieldClassName: t6,
    suffixClass: t7,
    fieldClassName: t8,
    balanceTitle
  } = t0;
  const isLogin = t1 === undefined ? false : t1;
  const hasError = t2 === undefined ? false : t2;
  const showActionButton = t3 === undefined ? false : t3;
  const inputStyle = t4 === undefined ? "secondaryOutline" : t4;
  const customInputClass = t5 === undefined ? "h-14 min-w-full max-w-[400px]" : t5;
  const customFieldClassName = t6 === undefined ? "px-1 text-start text-center" : t6;
  const suffixClass = t7 === undefined ? "h-fit text-charcoal-900 text-sm font-medium" : t7;
  const fieldClassName = t8 === undefined ? "w-full flex-1" : t8;
  const t = useTranslations();
  const {
    roundDown,
    roundDownNoFormat
  } = useRoundDown();
  const [internalError, setInternalError] = useState(null);
  let t9;
  if ($[0] !== value) {
    t9 = normalizePropValue(value);
    $[0] = value;
    $[1] = t9;
  } else {
    t9 = $[1];
  }
  const [displayValue, setDisplayValue] = useState(t9);
  const displayValueRef = useRef(displayValue);
  const userInputRef = useRef(false);
  const [hasValueChanged, setHasValueChanged] = useState(false);
  const forceDecimals = decimalLimit === 0 || decimalLimit === 8;
  const hasVisibleError = !!internalError || hasError;
  let t10;
  if ($[2] !== forceDecimals || $[3] !== roundDown || $[4] !== selectedCoin?.symbol || $[5] !== type) {
    t10 = amount => {
      if (type === "fiat") {
        return roundDown(amount, "IRT", forceDecimals);
      }
      return roundDown(amount, selectedCoin?.symbol ?? "", forceDecimals);
    };
    $[2] = forceDecimals;
    $[3] = roundDown;
    $[4] = selectedCoin?.symbol;
    $[5] = type;
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  selectedCoin?.symbol;
  const formatWithDecimalLimit = t10;
  let t11;
  if ($[7] !== forceDecimals || $[8] !== maxAllowed || $[9] !== onChange || $[10] !== roundDownNoFormat || $[11] !== selectedCoin?.symbol || $[12] !== type || $[13] !== walletBalance) {
    t11 = event => {
      event.preventDefault();
      if (walletBalance === undefined) {
        return;
      }
      const maxRaw = maxAllowed !== undefined ? Math.min(walletBalance, maxAllowed) : walletBalance;
      let finalValue;
      if (type === "fiat") {
        finalValue = Number(roundDownNoFormat(maxRaw, "IRT", forceDecimals));
      } else {
        finalValue = Number(roundDownNoFormat(maxRaw, selectedCoin?.symbol, forceDecimals));
      }
      const nextDisplayValue = balanceClickDisplayValue(finalValue);
      userInputRef.current = false;
      displayValueRef.current = nextDisplayValue;
      setDisplayValue(nextDisplayValue);
      onChange(finalValue);
      setHasValueChanged(true);
    };
    $[7] = forceDecimals;
    $[8] = maxAllowed;
    $[9] = onChange;
    $[10] = roundDownNoFormat;
    $[11] = selectedCoin?.symbol;
    $[12] = type;
    $[13] = walletBalance;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  selectedCoin?.symbol;
  const handleMaxClick = t11;
  const handleFocusSelectAll = _temp;
  const handleClickSelectAll = _temp2;
  let t12;
  if ($[15] !== decimalLimit || $[16] !== onChange || $[17] !== onEmpty) {
    t12 = nextValue => {
      const rawInput = nextValue === undefined || nextValue === null ? "" : String(nextValue);
      const raw = decimalLimit === 0 ? rawInput.replace(/,/g, "").replace(/[^\d-]/g, "") : rawInput;
      userInputRef.current = true;
      displayValueRef.current = raw;
      setDisplayValue(raw);
      if (raw === "") {
        if (onEmpty) {
          onEmpty();
        } else {
          onChange(0);
        }
        setHasValueChanged(true);
        return;
      }
      const parsed = parseRawValue(raw);
      if (parsed !== null) {
        onChange(parsed);
      }
      setHasValueChanged(true);
    };
    $[15] = decimalLimit;
    $[16] = onChange;
    $[17] = onEmpty;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const handleChange = t12;
  let t13;
  if ($[19] !== onKeyDown) {
    t13 = event_2 => {
      if (event_2.key === "Enter") {
        event_2.preventDefault();
        event_2.stopPropagation();
      }
      onKeyDown?.(event_2);
    };
    $[19] = onKeyDown;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const handleKeyDown = t13;
  let t14;
  let t15;
  if ($[21] !== errorTextMax || $[22] !== errorTextMin || $[23] !== hasValueChanged || $[24] !== maxAllowed || $[25] !== minAllowed || $[26] !== t || $[27] !== value) {
    t14 = () => {
      if (!hasValueChanged && (value === 0 || value === null)) {
        setInternalError(null);
        return;
      }
      if (minAllowed !== undefined && value && value < minAllowed) {
        setInternalError(errorTextMin || t("common.MIN_AMOUNT_ERROR", {
          min: minAllowed
        }));
      } else {
        if (maxAllowed !== undefined && value && value > maxAllowed) {
          setInternalError(errorTextMax || t("common.MAX_AMOUNT_ERROR", {
            max: maxAllowed
          }));
        } else {
          setInternalError(null);
        }
      }
    };
    t15 = [errorTextMax, errorTextMin, hasValueChanged, maxAllowed, minAllowed, t, value];
    $[21] = errorTextMax;
    $[22] = errorTextMin;
    $[23] = hasValueChanged;
    $[24] = maxAllowed;
    $[25] = minAllowed;
    $[26] = t;
    $[27] = value;
    $[28] = t14;
    $[29] = t15;
  } else {
    t14 = $[28];
    t15 = $[29];
  }
  useEffect(t14, t15);
  let t16;
  if ($[30] !== hasValueChanged || $[31] !== value) {
    t16 = () => {
      if (userInputRef.current && shouldPreserveUserInput(displayValueRef.current, value)) {
        userInputRef.current = false;
        return;
      }
      const nextDisplayValue_0 = normalizePropValue(value, hasValueChanged);
      displayValueRef.current = nextDisplayValue_0;
      setDisplayValue(nextDisplayValue_0);
      userInputRef.current = false;
    };
    $[30] = hasValueChanged;
    $[31] = value;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== decimalLimit || $[34] !== value) {
    t17 = [decimalLimit, value];
    $[33] = decimalLimit;
    $[34] = value;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  useEffect(t16, t17);
  const displayBalance = walletBalance ?? (isLogin ? 0 : undefined);
  let balanceDisplay = "-";
  if (balanceSymbol && displayBalance !== undefined) {
    let t18;
    if ($[36] !== balanceSymbol || $[37] !== displayBalance || $[38] !== formatWithDecimalLimit || $[39] !== roundDown || $[40] !== type) {
      t18 = type === "crypto" ? `${balanceSymbol} ${formatWithDecimalLimit(displayBalance)}` : `${roundDown(displayBalance, "IRT")} ${balanceSymbol}`;
      $[36] = balanceSymbol;
      $[37] = displayBalance;
      $[38] = formatWithDecimalLimit;
      $[39] = roundDown;
      $[40] = type;
      $[41] = t18;
    } else {
      t18 = $[41];
    }
    balanceDisplay = t18;
  }
  const error = internalError || (hasError ? errorTextMin || errorTextMax || null : null);
  const hint = value !== null && value !== undefined && value > 0 && !internalError && !hasError && hintText ? hintText : null;
  const t18 = hasVisibleError ? "secondaryOutlineError" : inputStyle;
  let t19;
  if ($[42] !== customFieldClassName || $[43] !== customInputClass || $[44] !== decimalLimit || $[45] !== displayValue || $[46] !== fieldClassName || $[47] !== handleChange || $[48] !== handleKeyDown || $[49] !== hasVisibleError || $[50] !== label || $[51] !== onBlur || $[52] !== onFocus || $[53] !== placeholder || $[54] !== prefix || $[55] !== suffix || $[56] !== suffixClass || $[57] !== t18) {
    t19 = {
      inputId: "amount",
      onKeyDown: handleKeyDown,
      label,
      style: t18,
      customInputClass,
      customFieldClassName,
      fieldClassName,
      placeholder,
      type: "currency",
      value: displayValue,
      onChange: handleChange,
      prefix,
      decimalsLimit: decimalLimit,
      suffix,
      suffixClass,
      hasError: hasVisibleError,
      onFocus,
      onBlur,
      handleFocus: handleFocusSelectAll,
      handleClickSelectAll
    };
    $[42] = customFieldClassName;
    $[43] = customInputClass;
    $[44] = decimalLimit;
    $[45] = displayValue;
    $[46] = fieldClassName;
    $[47] = handleChange;
    $[48] = handleKeyDown;
    $[49] = hasVisibleError;
    $[50] = label;
    $[51] = onBlur;
    $[52] = onFocus;
    $[53] = placeholder;
    $[54] = prefix;
    $[55] = suffix;
    $[56] = suffixClass;
    $[57] = t18;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  const t20 = balanceTitle ?? "\u0645\u0648\u062C\u0648\u062F\u06CC \u062F\u0631 \u062F\u0633\u062A\u0631\u0633";
  let t21;
  if ($[59] !== balanceDisplay || $[60] !== handleMaxClick || $[61] !== isLogin || $[62] !== onActionButtonClick || $[63] !== showActionButton || $[64] !== t20) {
    t21 = {
      visible: isLogin,
      title: t20,
      value: balanceDisplay,
      showActionButton,
      onMaxClick: handleMaxClick,
      onActionButtonClick
    };
    $[59] = balanceDisplay;
    $[60] = handleMaxClick;
    $[61] = isLogin;
    $[62] = onActionButtonClick;
    $[63] = showActionButton;
    $[64] = t20;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  let t22;
  if ($[66] !== error || $[67] !== hint || $[68] !== t19 || $[69] !== t21) {
    t22 = {
      field: t19,
      balance: t21,
      error,
      hint
    };
    $[66] = error;
    $[67] = hint;
    $[68] = t19;
    $[69] = t21;
    $[70] = t22;
  } else {
    t22 = $[70];
  }
  return t22;
}
function _temp2(event_1) {
  return event_1.currentTarget.value && event_1.currentTarget.select();
}
function _temp(event_0) {
  return event_0.target.value && event_0.target.select();
}